import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  TextField,
  CssBaseline,
  ThemeProvider,
  createTheme,
  useMediaQuery,
  FormHelperText,
} from "@mui/material";
import { Formik, Form, useField } from "formik";
import bannerImage from "../../assets/banner.jpeg";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { resetPassword } from "../../features/auth/authSlice";
import { showAppMessageToast } from "../../utils/toastUtils";

// This file is a self-contained React application that recreates the Reset Password page
// using Material-UI components. All external dependencies are mocked to
// make this a runnable, single-file example.

// MOCKING EXTERNAL DEPENDENCIES FOR A STANDALONE EXAMPLE
// const useNavigate = () => (path) => console.log(`Navigating to: ${path}`);
// const useLocation = () => ({ state: { email: "user@example.com" } });
// const useDispatch = () => () => ({ unwrap: () => Promise.resolve() });
// const useSelector = (selector) => ({ loading: false, error: null });
// const showAppMessageToast = ({ type, title, message }) => console.log(`${type}: ${title}\n${message}`);
// const bannerImage = "https://placehold.co/1080x1920/1A237E/fff?text=Reset+Password+Banner";
// const resetPassword = () => ({ type: 'mock/resetPassword' });

// Custom theme to match the colors from the login and register pages
const customTheme = createTheme({
  palette: {
    primary: {
      main: "#1976d2",
    },
    secondary: {
      main: "#ff9800",
      contrastText: "#fff",
    },
    scraptradeYellow: {
      main: "#F4C542",
      dark: "#E3AE27",
    },
    scraptradeDark: {
      main: "#2D2D2D",
    },
    background: {
      default: "#f4f6f8",
    },
  },
  typography: {
    fontFamily: "'Inter', sans-serif",
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: "8px",
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
          paddingTop: "12px",
          paddingBottom: "12px",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: "16px",
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: "#f0f4f9",
          borderRadius: "8px",
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#F4C542",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#E3AE27",
          },
          "&.Mui-focused .MuiInputBase-input::placeholder": {
            color: "#E3AE27",
            opacity: 1,
          },
          "&.Mui-focused .MuiInputLabel-root": {
            color: "#E3AE27",
          },
        },
        notchedOutline: {
          borderColor: "transparent",
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          "&.Mui-focused": {
            color: "#E3AE27",
          },
        },
      },
    },
  },
});

// A custom OTP input component with the requested highlight feature
const OtpInput = ({ name, length, ...props }) => {
  const [field, meta, helpers] = useField(name);
  const [otpValues, setOtpValues] = useState(new Array(length).fill(""));
  const inputRefs = useRef([]);

  useEffect(() => {
    // Sync Formik value with internal state
    helpers.setValue(otpValues.join(""));
  }, [otpValues, helpers]);

  const handleChange = (index, event) => {
    const { value } = event.target;
    if (/[^0-9]/.test(value) && value !== "") return; // Allow empty string for backspace

    const newOtpValues = [...otpValues];
    newOtpValues[index] = value.slice(-1); // Only take the last character

    setOtpValues(newOtpValues);

    // Focus next input field automatically
    if (value && index < length - 1) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, event) => {
    // Move to previous input on backspace if current is empty
    if (event.key === "Backspace" && !otpValues[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const errorText = meta.error && meta.touched ? meta.error : "";

  return (
    <Box
      sx={{ display: "flex", gap: 1, justifyContent: "center", mt: 2, mb: 1 }}
    >
      {otpValues.map((value, index) => (
        <TextField
          key={index}
          value={value}
          onChange={(e) => handleChange(index, e)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          inputRef={(el) => (inputRefs.current[index] = el)}
          variant="outlined"
          sx={{
            width: 48,
            "& .MuiOutlinedInput-root": {
              textAlign: "center",
              "& input": {
                textAlign: "center",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: customTheme.palette.scraptradeYellow.main,
              },
              "&.Mui-focused": {
                backgroundColor: "#fff",
              },
            },
            ...(value && {
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: customTheme.palette.scraptradeYellow.main,
                borderWidth: "2px",
              },
            }),
          }}
          inputProps={{
            maxLength: 1,
            style: { textAlign: "center" },
          }}
          error={!!errorText}
        />
      ))}
      {errorText && <FormHelperText error>{errorText}</FormHelperText>}
    </Box>
  );
};


// Validation Schema
const ResetPasswordSchema = Yup.object().shape({
  otp: Yup.string()
    .length(6, "OTP must be 6 digits")
    .required("OTP is required"),
  password: Yup.string()
    .min(6, "Password must be at least 6 characters")
    .required("New password is required"),
  password_confirmation: Yup.string()
    .oneOf([Yup.ref("password"), null], "Passwords must match")
    .required("Confirm your password"),
});

const ResetPassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;

  const { loading } = useSelector((state) => state.auth);

  const handleSubmit = async (values, { setSubmitting }) => {
    const payload = {
      email,
      otp: values.otp,
      password: values.password,
      password_confirmation: values.password_confirmation,
    };

    try {
      await dispatch(resetPassword(payload)).unwrap();
      
      showAppMessageToast({
        type: "success",
        title: "Password Reset",
        message: "Your password has been reset successfully. Please log in.",
      });
      navigate("/login");
    } catch (err) {
      console.error("Reset Password Error:", err);
      showAppMessageToast({
        type: "error",
        title: "Reset Password Failed",
        message: err?.message || "Failed to reset password. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const isMobile = useMediaQuery(customTheme.breakpoints.down("md"));

  if (!email) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6">
          Missing email information. Please go back to the previous page.
        </Typography>
        <Button 
          variant="contained" 
          sx={{ mt: 2 }}
          onClick={() => navigate("/forgot-password")}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", height: "100vh" }}>
      {/* Left Banner Section - hidden on mobile */}
      {!isMobile && (
        <Grid
          item
          md={8}
          sx={{
            backgroundImage: `url(${bannerImage})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            backgroundPosition: "center",
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "500px",
          }}
        >
          {/* Banner Overlay */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.55)",
              zIndex: 1,
            }}
          />
          {/* Banner Text Content */}
          <Box
            sx={{
              zIndex: 2,
              textAlign: "center",
              p: 4,
              minWidth: "786px",
              color: "white",
            }}
          >
            <Typography
              variant="h3"
              component="h1"
              fontWeight={700}
              sx={{ my: 1, fontSize: "2.5rem" }}
            >
              Scrap Trade
            </Typography>
            <Typography variant="body1" sx={{ color: "#ddd" }}>
              Secure your account by resetting your password.
            </Typography>
          </Box>
        </Grid>
      )}
      {/* Right Form Section */}
      <Grid
        item
        xs={12}
        md={4}
        component={Paper}
        elevation={0}
        square
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: customTheme.palette.background.default,
          flexGrow: 1,
          p: { xs: 2, md: 4 },
        }}
      >
        <Box
          sx={{
            my: { xs: 4, md: 8 },
            p: 4,
            width: "100%",
            maxWidth: 440,
            borderRadius: "16px",
            boxShadow: "0 0 30px rgba(0,0,0,0.05)",
            backgroundColor: "white",
          }}
        >
          <Typography component="h2" variant="h5" fontWeight={600} mb={3}>
            Reset Password
          </Typography>
          <Typography
            variant="body2"
            sx={{
              textAlign: "center",
              mb: 3,
              color: customTheme.palette.text.secondary,
            }}
          >
            Enter the 6-digit OTP sent to your email and your new password.
          </Typography>

          <Formik
            initialValues={{
              otp: "",
              password: "",
              password_confirmation: "",
            }}
            validationSchema={ResetPasswordSchema}
            onSubmit={handleSubmit}
          >
            {({ values, isSubmitting, touched, errors, handleChange }) => (
              <Form noValidate>
                <OtpInput name="otp" length={6} />
                {touched.otp && errors.otp && (
                  <FormHelperText error sx={{ textAlign: "center", mt: 1 }}>
                    {errors.otp}
                  </FormHelperText>
                )}

                <TextField
                  name="password"
                  label="New Password"
                  type="password"
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  placeholder="Enter new password"
                  value={values.password}
                  onChange={handleChange}
                  error={touched.password && Boolean(errors.password)}
                  helperText={touched.password && errors.password}
                  sx={{ mt: 3, mb: 1 }}
                />
                <TextField
                  name="password_confirmation"
                  label="Confirm Password"
                  type="password"
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  placeholder="Confirm new password"
                  value={values.password_confirmation}
                  onChange={handleChange}
                  error={touched.password_confirmation && Boolean(errors.password_confirmation)}
                  helperText={touched.password_confirmation && errors.password_confirmation}
                  sx={{ mb: 3 }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{
                    mt: 3,
                    mb: 2,
                    fontWeight: 600,
                    backgroundColor: customTheme.palette.scraptradeYellow.main,
                    color: "#2D3A27",
                    "&:hover": {
                      backgroundColor: customTheme.palette.scraptradeYellow.dark,
                    },
                  }}
                  disabled={isSubmitting || loading}
                >
                  {loading ? "Resetting..." : "Reset Password"}
                </Button>
              </Form>
            )}
          </Formik>
        </Box>
      </Grid>
    </Box>
  );
};

export default function App() {
  return (
    <ThemeProvider theme={customTheme}>
      <CssBaseline />
      <ResetPassword />
    </ThemeProvider>
  );
}
